/**
 * Servizio pagamento quota associativa - area riservata associati
 * Usa axios diretto + getAuthHeader (NON axiosInstance, che e per admin)
 */

import axios from 'axios';
import { getAuthHeader, getToken } from './otpAuthService';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

// ========================================
// TYPES
// ========================================

export interface VoceQuota {
  tipo: string;
  descrizione: string;
  importo: number;
}

export interface QuotaProvinciale {
  tipo: string;
  descrizione: string;
  importo: number;
}

export interface RataRID {
  numero: number;
  importo: number;
  scadenza: string;
}

export interface DettagliBancari {
  iban: string;
  banca: string;
  intestatario: string;
  bic: string;
}

/**
 * Gate di pagamento della quota nazionale calcolato dal server (campagna rinnovi).
 * Campi additivi: con un backend precedente mancano e si usa il gate storico.
 */
export interface GateQuotaServer {
  puoPagareQuota?: boolean;
  tipoPagamentoQuota?: 'iscrizione' | 'rinnovo' | null;
  motivoNonPagabile?: string | null;
  rinnoviAperti?: boolean;
  annoTesseramento?: number | null;
  /**
   * Anno della quota che l'associato può pagare / sta pagando. Per un rinnovo
   * anticipato (campagna dell'anno successivo aperta durante l'anno in corso)
   * è anno corrente + 1. Assente con backend precedente: si usa `anno`.
   */
  annoQuota?: number | null;
}

/** Anno della quota a cui si riferiscono testi e importi: annoQuota → anno → anno solare */
export const annoDellaQuota = (d: { annoQuota?: number | null; anno?: number | null } | null | undefined): number =>
  d?.annoQuota || d?.anno || new Date().getFullYear();

export interface PaymentSettingsResponse {
  success: boolean;
  data: GateQuotaServer & {
    anno: number;
    categoria: string;
    quotaNazionale: number;
    terminePagamento: string | null;
    fineTesseramento: string | null;
    rateRID: RataRID[];
    quoteIntegrative: VoceQuota[];
    quotaProvinciale: QuotaProvinciale | null;
    totale: number;
    dettagliBancari: DettagliBancari;
    metodiPagamento: { carta: boolean; bonifico: boolean; rid: boolean };
  };
}

export interface PaymentStatusData extends GateQuotaServer {
  hasPendingPayment: boolean;
  anno?: number;
  _id?: string;
  stato?: string;
  metodoPagamento?: string;
  importoTotale?: number;
  vociQuota?: VoceQuota[];
  rate?: Array<{
    numero: number;
    importo: number;
    scadenza: string;
    stato: string;
    dataPagamento?: string;
    confermatoIl?: string;
  }>;
  dataPagamento?: string;
  ricevutaId?: any;
  mandatoSepa?: {
    iban?: string;
    url?: string;
    uploadedAt?: string;
  };
  bonifico?: {
    distintivaUrl?: string;
    uploadedAt?: string;
  };
  createdAt?: string;
  pendingIntegrative?: Array<{
    _id: string;
    vociQuota: VoceQuota[];
    importoTotale: number;
    stato: string;
    metodoPagamento?: string;
    bonificoCaricato?: boolean;
  }>;
  payments?: any[];
}

export interface PaymentStatusResponse {
  success: boolean;
  data: PaymentStatusData;
}

export interface CreatePaymentIntentResponse {
  success: boolean;
  data: {
    clientSecret: string;
    paymentIntentId: string;
    amount: number;
    // Voci quota calcolate SERVER-SIDE (il client non le invia più)
    vociQuota?: VoceQuota[];
  };
}

export interface ConfirmPaymentResponse {
  success: boolean;
  data: {
    payment: any;
    receipt: any;
    // Possibile doppio addebito registrato: la segreteria verificherà
    duplicate?: boolean;
    message?: string;
  };
}

export interface GateQuota {
  /** L'associato può pagare ORA la quota nazionale */
  puoPagare: boolean;
  tipo: 'iscrizione' | 'rinnovo' | null;
  /** Campagna rinnovi aperta e quota dell'anno già pagata */
  rinnovato: boolean;
  motivo: string | null;
  anno: number;
}

/**
 * Gate unico lato client per la quota nazionale: usa la decisione del server
 * (`puoPagareQuota`) e, solo se il backend non la espone, il gate storico
 * (IN ISCRIZIONE + IN ATTIVAZIONE, nessun pagamento principale in corso).
 * Senza stato pagamenti caricato non si può pagare.
 * L'anno è quello della quota (`annoQuota`, anche corrente + 1 per un rinnovo
 * anticipato): «rinnovato» confronta annoTesseramento con quell'anno, mai con
 * l'anno solare, e il permesso di pagare resta sempre quello del server.
 */
export const valutaGateQuota = (
  status: PaymentStatusData | null | undefined,
  associate: { stato?: string; statoTesseramento?: string } | null | undefined
): GateQuota => {
  const anno = annoDellaQuota(status);
  if (!status || !associate) {
    return { puoPagare: false, tipo: null, rinnovato: false, motivo: null, anno };
  }
  const rinnovato = !!status.rinnoviAperti && status.annoTesseramento === anno;
  if (typeof status.puoPagareQuota === 'boolean') {
    const puoPagare = status.puoPagareQuota;
    return {
      puoPagare,
      tipo: puoPagare ? (status.tipoPagamentoQuota || 'iscrizione') : null,
      rinnovato,
      motivo: puoPagare ? null : (status.motivoNonPagabile || null),
      anno
    };
  }
  const puoPagare =
    associate.stato === 'IN ISCRIZIONE' &&
    associate.statoTesseramento === 'IN ATTIVAZIONE' &&
    (!status.hasPendingPayment || status.stato === 'annullato');
  return { puoPagare, tipo: puoPagare ? 'iscrizione' : null, rinnovato, motivo: null, anno };
};

// ========================================
// CARTA ADDEBITATA, REGISTRAZIONE DA COMPLETARE
// ========================================

/**
 * Pagamento con carta riuscito su Stripe la cui registrazione sul portale non è
 * ancora confermata. Resta nel browser finché la conferma non riesce: finché c'è,
 * la card non propone di pagare di nuovo (rischio doppio addebito).
 */
export interface CartaPendente {
  paymentIntentId: string;
  anno: number;
  ts: number;
  /** Pagamento integrativo pre-creato (assente per la quota nazionale) */
  paymentId?: string;
}

export const CARTA_PENDENTE_TTL_MS = 72 * 60 * 60 * 1000;

const chiaveCartaPendente = (associateId: string) => `unasca:tesseramento:carta-pendente:${associateId}`;

export const salvaCartaPendente = (associateId: string | undefined, voce: Omit<CartaPendente, 'ts'>): void => {
  if (!associateId) return;
  try {
    localStorage.setItem(chiaveCartaPendente(associateId), JSON.stringify({ ...voce, ts: Date.now() }));
  } catch {
    // localStorage non disponibile: resta la protezione lato server
  }
};

/** Con `paymentIntentId` rimuove la voce solo se riguarda quel PaymentIntent. */
export const rimuoviCartaPendente = (associateId: string | undefined, paymentIntentId?: string): void => {
  if (!associateId) return;
  try {
    const key = chiaveCartaPendente(associateId);
    if (paymentIntentId) {
      const raw = localStorage.getItem(key);
      if (!raw || JSON.parse(raw)?.paymentIntentId !== paymentIntentId) return;
    }
    localStorage.removeItem(key);
  } catch {
    // ignora
  }
};

/** Voce valida e più recente di 72 ore, altrimenti null (le voci scadute o corrotte vengono rimosse). */
export const leggiCartaPendente = (associateId: string | undefined): CartaPendente | null => {
  if (!associateId) return null;
  try {
    const raw = localStorage.getItem(chiaveCartaPendente(associateId));
    if (!raw) return null;
    const voce = JSON.parse(raw);
    const valida = voce
      && typeof voce.paymentIntentId === 'string' && voce.paymentIntentId
      && typeof voce.ts === 'number'
      && Date.now() - voce.ts < CARTA_PENDENTE_TTL_MS;
    if (!valida) {
      localStorage.removeItem(chiaveCartaPendente(associateId));
      return null;
    }
    return voce as CartaPendente;
  } catch {
    return null;
  }
};

/**
 * La conferma non potrà mai riuscire (PaymentIntent rimborsato o inesistente):
 * la voce va rimossa. Ogni altro errore è ritentabile.
 */
export const isConfermaCartaDefinitiva = (err: any): boolean => {
  const status = err?.response?.status;
  if (status === 404) return true;
  return status === 409 && /rimborsat/i.test(String(err?.response?.data?.message || ''));
};

/**
 * Lo stato pagamenti mostra già registrato il pagamento della voce: quota nazionale
 * confermata per l'anno oppure, per un'integrativa, quel pagamento confermato.
 */
export const isCartaPendenteRegistrata = (voce: CartaPendente, status: PaymentStatusData | null | undefined): boolean => {
  const payments: any[] = Array.isArray(status?.payments) ? (status as PaymentStatusData).payments as any[] : [];
  if (voce.paymentId) {
    return payments.some(p => String(p._id) === voce.paymentId && p.stato === 'confermato');
  }
  // L'anno di riferimento è quello della quota (annoQuota: corrente + 1 per un rinnovo anticipato)
  if (!status || annoDellaQuota(status) !== voce.anno) return false;
  return payments.some(p =>
    !p.isDuplicate && p.stato === 'confermato'
    && (typeof p.anno !== 'number' || p.anno === voce.anno)
    && (p.vociQuota || []).some((v: any) => v.tipo === 'nazionale'));
};

// ========================================
// API CALLS
// ========================================

/**
 * Recupera impostazioni pagamento per l'associato autenticato
 * (quote, importi, dettagli bancari)
 */
export const getPaymentSettings = async (): Promise<PaymentSettingsResponse> => {
  const { data } = await axios.get<PaymentSettingsResponse>(
    `${API_BASE}/associate-payment/settings`,
    { headers: getAuthHeader() as any }
  );
  return data;
};

/**
 * Stato del pagamento corrente (anno in corso)
 */
export const getPaymentStatus = async (): Promise<PaymentStatusResponse> => {
  const { data } = await axios.get<PaymentStatusResponse>(
    `${API_BASE}/associate-payment/status`,
    { headers: getAuthHeader() as any }
  );
  return data;
};

/**
 * Crea PaymentIntent Stripe per pagamento con carta.
 * Le voci quota sono calcolate SERVER-SIDE: si passa solo il paymentId
 * per i pagamenti integrativi pre-creati (altrimenti quota nazionale).
 */
export const createPaymentIntent = async (paymentId?: string | null): Promise<CreatePaymentIntentResponse> => {
  const { data } = await axios.post<CreatePaymentIntentResponse>(
    `${API_BASE}/associate-payment/create-payment-intent`,
    paymentId ? { paymentId } : {},
    { headers: getAuthHeader() as any }
  );
  return data;
};

/**
 * Conferma pagamento dopo successo Stripe.
 * Le voci quota sono calcolate SERVER-SIDE: non si inviano più dal client.
 */
export const confirmPayment = async (paymentIntentId: string): Promise<ConfirmPaymentResponse> => {
  const { data } = await axios.post<ConfirmPaymentResponse>(
    `${API_BASE}/associate-payment/confirm-payment`,
    { paymentIntentId },
    { headers: getAuthHeader() as any }
  );
  return data;
};

/**
 * Richiede pagamento via bonifico bancario
 * Invia distinta PDF come FormData
 */
export const requestBankTransfer = async (formData: FormData): Promise<{ success: boolean; data: any }> => {
  const token = getToken();
  const { data } = await axios.post(
    `${API_BASE}/associate-payment/request-bank-transfer`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return data;
};

/**
 * Richiede pagamento via RID (addebito diretto SEPA) con rate
 */
export const requestRid = async (iban: string, vociQuota: VoceQuota[]): Promise<{ success: boolean; data: any }> => {
  const { data } = await axios.post(
    `${API_BASE}/associate-payment/request-rid`,
    { iban, vociQuota },
    { headers: getAuthHeader() as any }
  );
  return data;
};

/**
 * Scarica mandato SEPA precompilato (PDF blob)
 */
export const downloadMandatoSepa = async (): Promise<Blob> => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE}/associate-payment/mandato-sepa/pdf`,
    {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    }
  );
  return response.data;
};

/**
 * Upload mandato SEPA firmato
 */
export const uploadMandatoSepa = async (formData: FormData): Promise<{ success: boolean; data: any }> => {
  const token = getToken();
  const { data } = await axios.post(
    `${API_BASE}/associate-payment/mandato-sepa/upload`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return data;
};

/**
 * Lista ricevute dell'associato
 */
export const getReceipts = async (): Promise<{ success: boolean; data: any[] }> => {
  const { data } = await axios.get(
    `${API_BASE}/associate-payment/receipts`,
    { headers: getAuthHeader() as any }
  );
  return data;
};

/**
 * Scarica PDF ricevuta (blob)
 */
export const downloadReceiptPdf = async (id: string): Promise<void> => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE}/associate-payment/receipts/${id}/pdf`,
    {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    }
  );

  // Trigger download
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;

  const contentDisposition = response.headers['content-disposition'];
  let filename = 'Ricevuta_UNASCA.pdf';
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?(.+)"?/i);
    if (match?.[1]) filename = match[1];
  }

  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Richiede aggiunta quota integrativa
 */
export const requestIntegrativa = async (settore: string): Promise<{ success: boolean; message: string }> => {
  const { data } = await axios.post(
    `${API_BASE}/associate-payment/request-integrativa`,
    { settore },
    { headers: getAuthHeader() as any }
  );
  return data;
};
